import { LinkOutlined } from "@ant-design/icons";
import { DOCS_URLS } from "@p0security/frontend-core";
import { AppPaths } from "@p0security/shared/routes/constants";
import { AssessmentScopeIntegration } from "@p0security/shared/types/assessment";
import { ButtonProps, Flex, Modal, Spacing, Typography } from "@p0security/ui";
import { widetype } from "@p0security/util/collections";
import { UserRole } from "components/Login";
import { AuthzButton } from "components/common/AuthzButton";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";

import { NewEnvironmentForm } from "../components/NewEnvironmentForm";
import { EnvironmentCreationProvider } from "../contexts/EnvironmentCreationContext";

export const CREATE_ENV_ROLES: UserRole[] = ["owner", "iamOwner"];
export const CreateEnvironment: React.FC<{
  installed: Record<AssessmentScopeIntegration, boolean>;

  buttonCopy?: JSX.Element | string;
  buttonStyle?: React.CSSProperties;
  buttonProps?: Omit<ButtonProps, "onClick">;
  onClick?: () => void;
}> = ({ installed, buttonCopy, buttonProps, onClick }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const closeModal = useCallback(() => setModalOpen(false), [setModalOpen]);
  const handleClick = useCallback(() => {
    setModalOpen(true);
    onClick?.();
  }, [onClick, setModalOpen]);

  const { orgSlug } = useParams();

  const installedIntegrations = useMemo(
    () =>
      widetype.keys(installed).filter((integration) => installed[integration]),
    [installed]
  );

  return (
    <>
      <Spacing asChild m="5px">
        <AuthzButton
          roles={CREATE_ENV_ROLES}
          type="primary"
          onClick={handleClick}
          {...buttonProps}
          /* eslint-disable-line react/forbid-component-props */ style={{
            padding: "0 5px 0 5px",
            ...buttonProps?.style,
          }}
        >
          {buttonCopy || "Create"}
        </AuthzButton>
      </Spacing>
      <Modal
        open={modalOpen}
        destroyOnHidden // Clear new assessment form on close
        maskClosable={false}
        footer={false}
        onCancel={closeModal}
      >
        {installedIntegrations.length > 0 ? (
          <>
            <Typography.Title level={4}>
              Create New Environment
            </Typography.Title>
            <EnvironmentCreationProvider>
              <NewEnvironmentForm
                setFormOpen={setModalOpen}
                installedIntegrations={installedIntegrations}
              />
            </EnvironmentCreationProvider>
          </>
        ) : (
          <div className="flex flex-col">
            <Typography.Title level={4}>
              No Available Integrations
            </Typography.Title>
            <Typography.Text>
              To create an environment, you&apos;ll first need to install an
              &ldquo;IAM assessment&rdquo; integration.
            </Typography.Text>
            <Flex className="place-content-center">
              <div>
                <Spacing asChild m="2em">
                  <Link
                    className="w-[12em] text-center"
                    to={`/o/${orgSlug}/${AppPaths.Integrations}`}
                  >
                    Install Integrations
                  </Link>
                </Spacing>
              </div>
              <div>
                <Spacing asChild m="2em">
                  <Link
                    className="w-[12em] text-center"
                    to={DOCS_URLS.IAM_ASSESSMENT_RUNNING}
                    target="_blank"
                  >
                    Read Docs <LinkOutlined />
                  </Link>
                </Spacing>
              </div>
            </Flex>
          </div>
        )}
      </Modal>
    </>
  );
};
